<template>
  <BaseModal :is-open="show" @close="close">
    <div class="mt-10 text-left sm:mt-0">
      <div v-if="isPartoo">
        <PartooIframe />
      </div>
      <form v-else-if="!authenticationUrl">
        <div>
          <h3 class="text-lg font-medium leading-6 text-foreground">
            {{ $t(`pages.review.reviewSettingsPage.${reviewPlatform.slug}.title`) }}
          </h3>
          <p class="mt-1 max-w-2xl text-sm text-muted-foreground">
            {{ $t(`pages.review.reviewSettingsPage.${reviewPlatform.slug}.subtitle`) }}
          </p>
        </div>
        <div class="mt-6 space-y-6 sm:mt-5 sm:space-y-5">
          <div class="sm: items-top grid sm:grid-cols-4 sm:border-t sm:border-border sm:pt-5">
            <label for="platformUrl" class="block text-sm text-foreground sm:col-span-1 sm:mt-px sm:pt-2">
              {{ $t(`pages.review.reviewSettingsPage.${reviewPlatform.slug}.label`) }}
            </label>
            <div class="mt-1 sm:col-span-3 sm:mt-0 sm:pl-6">
              <AppGooglePlaceIdAutoComplete v-if="showGooglePlaceId" v-model="googlePlaceId" />
              <template v-else>
                <AppFormInput
                  id="platformUrl"
                  v-model="linkValue"
                  :placeholder="placeholderText"
                  type="text"
                  name="platformUrl"
                  class="w-full"
                >
                  <template #icon-left>
                    <img :src="`${imgUrl}${reviewPlatform.slug}.png`" class="h-5 w-5" />
                  </template>
                </AppFormInput>
              </template>
            </div>
          </div>
        </div>
        <div class="pt-5">
          <div class="flex justify-end space-x-2">
            <Button type="button" variant="outline" @click="close">
              {{ $t('ui.confirmModal.buttons.cancel') }}
            </Button>
            <Button type="button" variant="default-primary" :disabled="processing" @click="handleSubmit">
              <Loader2 v-if="processing" class="animate-spin" />
              {{ $t('pages.review.reviewSettingsPage.setup') }}
            </Button>
          </div>
        </div>
      </form>
      <div v-else>
        <iframe class="iframe" :src="authenticationUrl" />
      </div>
    </div>
  </BaseModal>
</template>

<script>
import { BaseModal } from '@/ui/dialog'
import { Button } from '@/ui/button'
import { Loader2 } from 'lucide-vue-next'
import AppGooglePlaceIdAutoComplete from '@/components/ui/AppGooglePlaceIdAutoComplete.vue'
import PartooIframe from './PartooIframe.vue'
import useEvent from '@/composables/event'

export default {
  components: {
    Button,
    Loader2,
    AppGooglePlaceIdAutoComplete,
    PartooIframe,
    BaseModal
  },
  props: {
    show: {
      type: Boolean,
      required: true
    },
    reviewPlatform: {
      type: Object,
      required: false,
      default: null
    },
    reviewManager: {
      type: Object,
      required: false,
      default: null
    },
    store: {
      type: Object,
      required: false,
      default: null
    }
  },
  emits: ['close'],
  setup() {
    const { send } = useEvent()
    const notification = useNotification()

    return {
      send,
      notification
    }
  },
  data() {
    return {
      processing: false,
      googlePlaceId: null,
      linkValue: null
    }
  },
  computed: {
    showGooglePlaceId() {
      return this.reviewPlatform.slug === 'google' && !this.authenticationUrl
    },
    authenticationUrl() {
      const reviewManagerSlug = this.reviewManager?.slug
      if (!reviewManagerSlug) {
        return null
      }

      return this.reviewPlatform.reviewManagers[reviewManagerSlug].authenticationUrl
    },
    isPartoo() {
      return this.reviewManager?.slug === 'partoo'
    },
    placeholderText() {
      switch (this.reviewPlatform.slug) {
        case 'tripadvisor':
          return 'https://www.tripadvisor.fr/Restaurant_Review-g1-d2-Reviews-Restaurant_Exemple-Paris.html'
        case 'facebook':
          return 'https://www.facebook.com/*'
        default:
          return null
      }
    },
    imgUrl() {
      return `${import.meta.env.VITE_AWS_STORAGE_URL}/external-websites/`
    },
    hasErrors() {
      switch (this.reviewPlatform.slug) {
        case 'tripadvisor':
          return !this.linkValue.includes('Restaurant_Review') || !this.linkValue.includes('www.tripadvisor')
        case 'facebook':
          return !this.linkValue.includes('facebook')
        default:
          return false
      }
    }
  },
  watch: {
    store() {
      this.updateData()
    },
    reviewPlatform() {
      this.updateData()
    }
  },
  created() {
    if (this.store) {
      this.googlePlaceId = this.store.googlePlaceId
    }
  },
  methods: {
    updateData() {
      this.linkValue = ''
      if (this.storeReviewPlatform) {
        this.linkValue = this.storeReviewPlatform.linkValue
      }

      if (this.store) {
        this.googlePlaceId = this.store.googlePlaceId
      }
    },
    handleSubmit() {
      if (this.hasErrors) {
        this.notification.create('error', {
          text: this.$t('pages.review.reviewSettingsPage.error.incorrectUrl'),
          persist: true
        })
      } else {
        this.submit()
      }
    },
    notify(type) {
      let text
      switch (type) {
        case 'success':
          text = this.$t('pages.settings.storeDetailPage.notifications.update.inProgress')
          break
        default:
          text = this.$t('notifications.submit')
          break
      }

      this.notification.create(type, {
        text: text,
        timeout: 7000
      })
    },
    submit() {
      if (this.showGooglePlaceId) {
        this.axios
          .patch(`stores/${this.store.id}/google-place-id`, {
            googlePlaceId: this.googlePlaceId
          })
          .then(() => {
            this.notify('success')
          })
          .catch(() => this.notify('error'))
        this.close()
        return
      }

      this.axios
        .post(`stores/${this.store.id}/review-platforms`, {
          reviewPlatformId: this.reviewPlatform.id,
          linkValue: this.linkValue,
          linkType: this.reviewPlatform.linkType
        })
        .then(() => {
          this.send('activated-reviews-platform')
          this.notify('success')
        })
        .catch(() => this.notify('error'))

      this.close()
    },
    close() {
      this.axios
        .post(`/stores/${this.store.id}/review-platforms/refresh`)
        .catch(() => {
          this.notification.create('error', {
            text: this.$t('pages.settings.storeDetailPage.notifications.update.error')
          })
        })
        .finally(() => {
          this.$emit('close')
        })
    }
  }
}
</script>

<style>
.iframe {
  height: 80vh;
  width: 100%;
}
</style>
